// Entry point. React consumes two Math.random() values while its modules initialise (random keys
// for its internal DOM properties). The legacy app made no random call before the shuffle, and the
// e2e fixtures seed Math.random to pin deals, so under the tests those two calls must not advance the
// shared stream: React loads with a constant generator, then the real one is restored before the app
// runs. Only under the tests (2026-11-01): for a player the constant reached the ad and consent scripts
// too, for as long as the start took, and no deal depends on it.
// The error reports (ui/errorReport.ts) start first, so a failure while the app loads is reported too.
// The start goes in two steps without a top-level await (owner 2026-10-25: Safari 14 and Chrome 83 do not know it):
// app-prelude.ts (the options and the language file), then app-entry.tsx. Each step is written to
// window.__bootStages, which the page template's start watchdog sends when the game has not started after 30 s.
import { installErrorReports, reportError } from './ui/errorReport';

declare global {
  interface Window { __bootStages?: Record<string, number>; __bootPending?: Set<string> }
}

const stage = (name: string) => {
  (window.__bootStages ??= {})[name] = Math.round(performance.now());
};

installErrorReports();
stage('main');
const realRandom = Math.random;
if (window.__E2E__) Math.random = () => 0.5;

/** A chunk that did not arrive: a deploy while the page was open (its files are gone), a broken connection. */
const LOAD_FAILED = /dynamically imported module|Importing a module script failed|Failed to fetch|error loading/i;
const RELOAD_KEY = 'solitaire_storage:boot-reload';
/** The waits before the reloads after a chunk that did not load (no message to the player, owner 2026-10-25: "the game
 *  should just work"): at once, then after 5 s and 15 s; a fourth failure within 5 minutes gives up. The last reload
 *  comes about 20 s after the first failure, after a deploy's switch-over (10-20 s, .ai/gotchas.md). */
const RELOAD_WAITS = [0, 5000, 15_000];

/** The wait before the next reload (null when the page has reloaded enough, or there is no storage to count in) and
 *  how many reloads came before this failure within the 5 minutes. */
function nextReload(): { wait: number | null; before: number } {
  try {
    const [since, count] = (sessionStorage.getItem(RELOAD_KEY) ?? '').split(':').map(Number);
    const n = since && Date.now() - since < 300_000 ? count || 0 : 0;
    if (n >= RELOAD_WAITS.length) return { wait: null, before: n };
    sessionStorage.setItem(RELOAD_KEY, `${n ? since : Date.now()}:${n + 1}`);
    return { wait: RELOAD_WAITS[n] ?? null, before: n };
  } catch {
    return { wait: null, before: 0 }; // no storage: no way to stop a reload loop
  }
}

/** A chunk that neither comes nor fails within this time (2026-10-31: 12 starts in 30 days stopped at `main` with the
 *  prelude never even requested; a blocked request has no error to catch): treated like a failed load, the page reloads. */
const IMPORT_MS = 20_000;
/** The entry chunk has low priority (the page template), so on a slow line it ends after the table's picture: at about
 *  125 kb/s the two take 20 s, and the old limit would have reloaded a page that was only slow (2026-11-01). */
const ENTRY_IMPORT_MS = 30_000;
const within = <T,>(what: string, p: Promise<T>, ms = IMPORT_MS): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Failed to fetch dynamically imported module in ${ms / 1000} s: ${what}`)), ms);
    p.then((v) => {
      clearTimeout(timer);
      resolve(v);
    }, (e: unknown) => {
      clearTimeout(timer);
      reject(e as Error); // as thrown: a start step that throws no Error is reported as such (BOOT-016)
    });
  });

within('app-prelude', import('./app-prelude'))
  .then((p) => {
    stage('prelude');
    return p.languageReady;
  })
  .then(() => {
    stage('language');
    return within('app-entry', import('./app-entry'), ENTRY_IMPORT_MS);
  })
  .then(() => stage('entry'))
  .catch((err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;
    // what failed where: the start steps reached, and what was thrown when it was no Error (2026-10-25: Firefox
    // reported "the app did not start: undefined" with nothing else)
    const where = {
      steps: { ...window.__bootStages },
      thrown: err instanceof Error ? err.name : err === null ? 'null' : typeof err === 'object' ? (err as object).constructor?.name ?? 'object' : typeof err,
    };
    console.error(err); // not rethrown: an unhandled rejection would be reported a second time
    if (err instanceof SyntaxError) {
      // the browser cannot read our code (older than the build target, frontend/vite.config.ts): the report tells
      // which browsers the target must reach
      reportError('boot', `old browser, the app did not start: ${message}`, stack, where);
    } else if (LOAD_FAILED.test(message)) {
      const { wait, before } = nextReload();
      // only when no reload is left (2026-10-25: each deploy's switch-over gave a burst of these, and a reload fixed
      // them; Safari remembers the failed file for the reload at once and fetches it again only on a later one; with a
      // report after the second reload, still inside the switch-over, one iPhone sent 4)
      if (wait === null) reportError('boot', `a part of the app did not load, ${before} reload(s) did not help: ${message}`, stack, { ...where, reloadIn: wait });
      if (wait !== null) setTimeout(() => location.reload(), wait);
    } else {
      reportError('boot', `the app did not start: ${message}`, stack, where);
    }
  })
  .finally(() => {
    if (window.__E2E__) Math.random = realRandom;
  });
